"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * The ONE dropdown-menu kit for the finance toolbars. CategoryMenu and TagMenu
 * are built from these pieces, so a behavior change (dismissal, the hover
 * "Only" affordance, checkbox styling) lands in one place:
 *
 *   useDismissableMenu  open state + close on outside click / Escape
 *   MenuTrigger         the pill button with the chevron
 *   MenuPanel           the floating panel under the trigger
 *   MenuCheckRow        a checkbox row — optional expand toggle, indent,
 *                       tri-state box, and a hover-revealed "Only" button
 *                       that narrows the selection to just that row
 *   TriCheckbox         a checkbox that can render indeterminate
 */

export function useDismissableMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return { open, setOpen, toggle: () => setOpen((o) => !o), ref };
}

/** Trigger padding + text size shared by every toolbar pill (matches the
 * Segmented pills / range picker); hosts may override per instance. */
export const MENU_TRIGGER_SIZE = "px-3 py-1.5 text-xs";

export function MenuTrigger({
  open,
  onClick,
  children,
  sizeClass = MENU_TRIGGER_SIZE,
  className = "",
}: {
  open: boolean;
  onClick: () => void;
  children: ReactNode;
  sizeClass?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border border-edge bg-surface-1 font-medium text-ink-2 hover:bg-surface-3/60 ${sizeClass} ${className}`}
    >
      {children}
      <svg viewBox="0 0 20 20" className="h-3.5 w-3.5 shrink-0 text-ink-3" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
        <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

export function MenuPanel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div role="menu" className={`absolute left-0 top-full z-50 mt-1 w-72 rounded-xl border border-edge bg-surface-1 shadow-lg ${className}`}>
      {children}
    </div>
  );
}

export function TriCheckbox({
  checked,
  indeterminate = false,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="h-3.5 w-3.5 shrink-0 accent-[var(--accent)]"
    />
  );
}

/**
 * One checkbox row. `onOnly` adds the hover-revealed "Only" button at the
 * row's right edge (always visible on touch screens, where there is no
 * hover, and when focused from the keyboard). The whole row is the label, so
 * clicking anywhere toggles; "Only" stops the click from reaching it.
 */
export function MenuCheckRow({
  label,
  checked,
  indeterminate = false,
  onToggle,
  onOnly,
  onlyLabel = "Only",
  indent = 0,
  emphasis = false,
  expand,
  dataKey,
}: {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onToggle: () => void;
  onOnly?: () => void;
  onlyLabel?: string;
  /** Nesting depth; each level indents by 16px. */
  indent?: number;
  /** Bold label (group / top-level rows). */
  emphasis?: boolean;
  /** Expand/collapse chevron for rows with children. */
  expand?: { open: boolean; onToggle: () => void };
  /** data-menu-row attribute for tests and analytics. */
  dataKey?: string;
}) {
  return (
    <div
      className="group flex items-center gap-1 rounded-lg py-1.5 pr-2 hover:bg-surface-3/50"
      style={{ paddingLeft: 8 + indent * 16 }}
      data-menu-row={dataKey ?? label}
    >
      {expand ? (
        <button
          type="button"
          aria-label={expand.open ? `Collapse ${label}` : `Expand ${label}`}
          onClick={expand.onToggle}
          className="w-4 shrink-0 text-ink-3 hover:text-ink"
        >
          {expand.open ? "▾" : "▸"}
        </button>
      ) : (
        <span className="w-4 shrink-0" />
      )}
      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
        <TriCheckbox checked={checked} indeterminate={indeterminate} onChange={onToggle} />
        <span className={`min-w-0 truncate text-sm ${emphasis ? "font-medium" : ""}`}>{label}</span>
      </label>
      {onOnly && (
        <button
          type="button"
          aria-label={`${onlyLabel} ${label}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOnly();
          }}
          className="shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-accent-ink opacity-0 hover:bg-surface-3 focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
        >
          {onlyLabel}
        </button>
      )}
    </div>
  );
}
